/** Supply operations (spec §11). */

import { z } from "zod";
import { readOp, writeOp, zEpochMs, zId } from "./common.js";

/**
 * B56 (spec §16 E1): ordered route stops. The SERVICE carries the route's
 * truth (key + display name + optional location); the SCHEDULE references
 * the same keys with per-run timing offsets (minutes after departure; the
 * first stop IS the departure). Present (min 2) makes every occurrence a
 * run of per-leg slots — the domain constructor walls ordering, uniqueness,
 * and the id-grammar characters.
 */
/**
 * B387: a WGS-84 coordinate for a location. Optional everywhere it appears —
 * locations are not an entity in Phase 1, so a coordinate is something an
 * operator supplies for the ones they care about, and absence is a value the
 * map depends on (it draws a measured pin or none, never an invented one).
 * The ranges are the globe's, so a transposed lat/lng pair outside them is
 * refused at the door rather than stored.
 */
const zServiceGeo = z
  .object({ lng: z.number().min(-180).max(180), lat: z.number().min(-90).max(90) })
  .strict();

const zServiceStop = z
  .object({
    key: z.string().min(1),
    name: z.string().min(1),
    locationId: zId.optional(),
    /** B387: this stop's own coordinate. */
    geo: zServiceGeo.optional(),
  })
  .strict();
const zServiceStops = z.array(zServiceStop).min(2);
const zScheduleStop = z
  .object({ key: z.string().min(1), offsetMinutes: z.number().int().nonnegative(), locationId: zId.optional() })
  .strict();
const zScheduleStops = z.array(zScheduleStop).min(2);

export const serviceCreate = writeOp({
  name: "service.create",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    serviceId: zId,
    name: z.string().min(1),
    locationId: zId,
    kind: z.string().min(1).optional(),
    /** B56 (E1): ordered route stops — the route's truth lives on the service. */
    stops: zServiceStops.optional(),
    /** B387: the home location's coordinate. */
    geo: zServiceGeo.optional(),
  },
  response: z.object({ serviceId: zId }).strict(),
  exampleRequest: { serviceId: "svc-1", name: "Harbor cruise", locationId: "loc-1" },
  exampleResponse: { serviceId: "svc-1" },
});

export const serviceUpdate = writeOp({
  name: "service.update",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    serviceId: zId,
    patch: z
      .object({
        name: z.string().min(1).optional(),
        locationId: zId.optional(),
        kind: z.string().optional(),
        /** B56 (E1): replaces the whole ordered stop list. */
        stops: zServiceStops.optional(),
        /**
         * B387: sets the coordinate. Clearing is its own flag rather than a
         * `null`, because the MCP membrane's converter (B22) translates a
         * CLOSED vocabulary — strict objects, scalars, enums, arrays,
         * discriminated unions, records — and `ZodNullable` is not in it. It
         * refused this contract outright, which is the wall doing its job:
         * every §11 operation is also an MCP tool schema, and widening the
         * converter for one optional field would widen it for every contract.
         */
        geo: zServiceGeo.optional(),
        /** B387: `true` forgets the coordinate; refused together with `geo`. */
        clearGeo: z.boolean().optional(),
      })
      .strict(),
  },
  response: z.object({ serviceId: zId }).strict(),
  exampleRequest: { serviceId: "svc-1", patch: { name: "Sunset cruise" } },
  exampleResponse: { serviceId: "svc-1" },
});

/**
 * B510 leg 3 (class-B default, 2026-09-24) - withdraw the service. Flips the
 * row RETIRED (services' first lifecycle; absent = ACTIVE): it leaves the
 * service.list roster (the device-enrollment location picker rides it) while
 * get/getMany keep answering for history, and service.update refuses it
 * thereafter. Refuses while an UN-RETIRED schedule still names the service
 * (naming the schedules) - retire the schedules first (schedule.retire).
 * Reversal: delete the retire ops + the flag (the B510 line).
 */
export const serviceRetire = writeOp({
  name: "service.retire",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: { serviceId: zId },
  response: z.object({ serviceId: zId, status: z.literal("RETIRED") }).strict(),
  exampleRequest: { serviceId: "svc-1" },
  exampleResponse: { serviceId: "svc-1", status: "RETIRED" },
});

/**
 * B165 — the service roster, and the reason it exists: a tenant's real
 * LOCATIONS are not an entity anywhere in Phase 1. `locationId` is a plain
 * field on a service and on its B56 route stops, so "where does this tenant
 * operate" has exactly one honest source — the services themselves. The
 * device-enrollment location picker rides this read instead of asking an
 * operator to type an id (FORMS.md R7).
 *
 * Seat and scope are the service WRITES' own (the B80/B92(3) precedent
 * B164 followed — no authorizer amendment); ascending by serviceId; zero
 * services is an honest empty list, never an error. Tenant-fenced by
 * construction: the store is keyed by tenantId.
 */
export const serviceList = readOp({
  name: "service.list",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      services: z.array(
        z
          .object({
            serviceId: zId,
            name: z.string().min(1),
            locationId: zId,
            kind: z.string().min(1).optional(),
            stops: zServiceStops.optional(),
            /** B387: present only where an operator has given the location one. */
            geo: zServiceGeo.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    services: [{ serviceId: "svc-1", name: "Harbor cruise", locationId: "loc-1", kind: "ferry" }],
  },
});

/**
 * B20a (ruling 2026-07-11): the schedule contract carries B15's full §4.3
 * shape — recurrence (day × time cross product), slot template (pools,
 * duration, sales cutoff), blackout dates. The pre-B20a (days, time,
 * capacity) triples could not express duration or pools, so a §11-created
 * schedule could never materialize a slot.
 */
const zTemplatePool = z
  .object({
    /** Stable key within the schedule (e.g. "seats") — becomes the pool id suffix. */
    key: z.string().min(1),
    name: z.string().min(1),
    capacity: z.number().int().nonnegative(),
  })
  .strict();
const zSlotTemplate = z
  .object({
    pools: z.array(zTemplatePool).min(1),
    durationMinutes: z.number().int().positive(),
    /** Sales cutoff, minutes before start (§5.4 close trigger). Default 0 = at start. */
    cutoffMinutesBeforeStart: z.number().int().nonnegative().optional(),
    /** B615 leg 3b — per-showtime ages: days × times → minAge (the product's age otherwise). */
    minAgeRules: z
      .array(
        z
          .object({
            daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1),
            times: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)).min(1),
            minAge: z.number().int().min(1).max(99),
          })
          .strict(),
      )
      .max(20)
      .optional(),
  })
  .strict();
const zRecurrence = z
  .object({
    /** 0 = Sunday … 6 = Saturday, in the schedule's time zone. */
    daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1),
    /** Local wall-clock times, "HH:MM" 24h. */
    times: z.array(z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/)).min(1),
    /** B432 — optional season bounds, local "YYYY-MM-DD", inclusive. */
    fromDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    untilDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();
// B432 — from ≤ until is enforced by the DOMAIN constructor (RangeError →
// 400 at the handler), not a zod .refine: ZodEffects is a construct the
// MCP membrane's schema translator refuses, measured the day this landed.
const zBlackoutDates = z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/));

export const scheduleCreate = writeOp({
  name: "schedule.create",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    scheduleId: zId,
    serviceId: zId,
    /** IANA time zone the recurrence is expressed in. */
    timezone: z.string().min(1),
    recurrence: zRecurrence,
    template: zSlotTemplate,
    blackoutDates: zBlackoutDates.optional(),
    /** B56 (E1): route stops with per-run timing offsets — present materializes one slot PER LEG. */
    stops: zScheduleStops.optional(),
  },
  response: z.object({ scheduleId: zId }).strict(),
  exampleRequest: {
    scheduleId: "sch-1",
    serviceId: "svc-1",
    timezone: "America/Los_Angeles",
    recurrence: { daysOfWeek: [6], times: ["09:00", "14:00"] },
    template: {
      pools: [
        { key: "seats", name: "Seats", capacity: 100 },
        { key: "vehicle-deck", name: "Vehicle deck", capacity: 20 },
      ],
      durationMinutes: 90,
      cutoffMinutesBeforeStart: 30,
    },
  },
  exampleResponse: { scheduleId: "sch-1" },
});

export const scheduleUpdate = writeOp({
  name: "schedule.update",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    scheduleId: zId,
    patch: z
      .object({
        timezone: z.string().min(1).optional(),
        recurrence: zRecurrence.optional(),
        template: zSlotTemplate.optional(),
        blackoutDates: zBlackoutDates.optional(),
        /** B56 (E1): replaces the whole ordered stop list; regeneration handles the route edit. */
        stops: zScheduleStops.optional(),
      })
      .strict(),
  },
  response: z.object({ scheduleId: zId, regeneratedSlots: z.number().int().nonnegative() }).strict(),
  exampleRequest: { scheduleId: "sch-1", patch: { timezone: "America/New_York" } },
  exampleResponse: { scheduleId: "sch-1", regeneratedSlots: 12 },
});

/**
 * B510 leg 2 (class-B default, 2026-09-24) - withdraw the recurrence. Flips
 * the schedule RETIRED: it leaves the materializer worklist (no new slots),
 * the schedule.list roster and the B31c card binding, and schedule.update
 * refuses it thereafter. Refuses while a future ON_SALE slot of the
 * schedule exists, naming the slots. Past slots stay history; sold tickets
 * stay valid. Reversal: delete the retire ops + the flag (the B510 line).
 */
export const scheduleRetire = writeOp({
  name: "schedule.retire",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: { scheduleId: zId },
  response: z.object({ scheduleId: zId, status: z.literal("RETIRED") }).strict(),
  exampleRequest: { scheduleId: "sch-1" },
  exampleResponse: { scheduleId: "sch-1", status: "RETIRED" },
});

/**
 * B193 slice 1 — the schedule roster. B25 made `slot.list` require a
 * `scheduleId` and B20a completed the schedule shape, but nothing ever
 * listed a tenant's schedules through §11: the admin screen asked the
 * operator to TYPE one (the R7/R13 finding this read exists to retire).
 *
 * Seat: the WRITES' own (`supply:write`, STAFF:ADMIN) — `service.list`'s
 * B165 precedent, and B92's deny-by-default stance for `audit.list`. A
 * buyer picks SLOTS (B20b(b)); the recurrence that made them is back-office
 * truth and stays behind the administration seat.
 *
 * The answer carries the STORED truth explicitly: the domain constructor
 * stores `cutoffMinutesBeforeStart: 0` and `blackoutDates: []` when create
 * omits them, so the read states them rather than dropping keys every
 * caller would then re-derive a default for. Only B56 `stops` is genuinely
 * absent-or-present — a schedule with no route is not one with an empty one.
 *
 * One honest limit on that stance: `template` REUSES `zSlotTemplate` (shared
 * with create/update, where the cutoff is legitimately optional), so the
 * schema alone does not force the cutoff to be present in an answer — the
 * handler and its test do. Reuse is still right: a response-only copy of the
 * template shape is a second place for §4.3 to drift.
 */
export const scheduleList = readOp({
  name: "schedule.list",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      schedules: z.array(
        z
          .object({
            scheduleId: zId,
            serviceId: zId,
            timezone: z.string().min(1),
            /** The domain's own ScheduleStatus, exactly — no state the machine cannot be in. */
            status: z.enum(["DRAFT", "PUBLISHED"]),
            recurrence: zRecurrence,
            template: zSlotTemplate,
            blackoutDates: zBlackoutDates,
            /** B56 (E1): present ⇒ every occurrence is a run of per-leg slots. */
            stops: zScheduleStops.optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    schedules: [
      {
        scheduleId: "sch-1",
        serviceId: "svc-1",
        timezone: "America/Los_Angeles",
        status: "PUBLISHED",
        recurrence: { daysOfWeek: [6], times: ["09:00", "14:00"] },
        template: {
          pools: [{ key: "seats", name: "Seats", capacity: 100 }],
          durationMinutes: 90,
          cutoffMinutesBeforeStart: 30,
        },
        blackoutDates: [],
      },
    ],
  },
});

const zSlotSummary = z
  .object({
    slotId: zId,
    start: zEpochMs,
    end: zEpochMs,
    status: z.enum(["DRAFT", "ON_SALE", "CLOSED", "COMPLETED", "CANCELLED"]),
    /** B615 leg 3b — this occurrence's own age, where its schedule's rows set one (else the product's stands). */
    minAge: z.number().int().min(1).max(99).optional(),
  })
  .strict();

/**
 * B25 decision (2026-07-11): scheduleId is REQUIRED — listing is driven by the
 * schedule partition (B15's deterministic ids); serviceId/from/to are filters.
 * Service-wide listing without a schedule arrives with B27's projections.
 */
/** B20b(b) ruling 2026-07-14: buyers pick slots (§7 step 1) — CUSTOMER joins all three slot reads. */
export const slotList = readOp({
  name: "slot.list",
  permission: "supply:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK", "API_KEY"],
  fields: { scheduleId: zId, serviceId: zId.optional(), from: zEpochMs.optional(), to: zEpochMs.optional() },
  response: z.object({ slots: z.array(zSlotSummary) }).strict(),
  exampleRequest: { scheduleId: "sch-1" },
  exampleResponse: { slots: [{ slotId: "s-1", start: 1_780_000_000_000, end: 1_780_003_600_000, status: "ON_SALE" }] },
});

export const slotGet = readOp({
  name: "slot.get",
  permission: "supply:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK", "API_KEY"],
  fields: { slotId: zId },
  response: z.object({ slot: zSlotSummary.optional() }).strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: { slot: { slotId: "s-1", start: 1_780_000_000_000, end: 1_780_003_600_000, status: "ON_SALE" } },
});

/** §11: availability exposes available units ONLY — never raw sold/held. */
export const slotAvailability = readOp({
  name: "slot.availability",
  permission: "supply:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK", "API_KEY"],
  fields: { slotId: zId },
  response: z
    .object({ slotId: zId, pools: z.array(z.object({ poolId: zId, available: z.number().int().nonnegative() }).strict()) })
    .strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: { slotId: "s-1", pools: [{ poolId: "pool-1", available: 42 }] },
});

/**
 * B31h(a) (RULING 2026-07-24 #1) — the exact-fit calendar read, batched over
 * a schedule so a calendar paints in ONE round trip. OPTIONAL party
 * (variantId → qty): when present, the SERVER aggregates it through the
 * product's §2.4 consumption matrix and answers a leak-free verdict per
 * slot — `fits` plus the same available-units summary slot.availability
 * exposes today. The matrix never crosses the wire (the B20b stance).
 * Journeys (spec §16 E1): candidate slots are the run's BOARDING legs and
 * the verdict spans EVERY leg of the O-D. With a party, channelId resolves
 * the active catalog version (versionId pins one); without a party this is
 * a pure batched availability read and neither is consulted.
 */
export const slotFit = readOp({
  name: "slot.fit",
  permission: "supply:read",
  principals: ["CUSTOMER", "RESELLER", "STAFF", "DEVICE:VALIDATOR", "DEVICE:KIOSK"],
  fields: {
    scheduleId: zId,
    productId: zId,
    channelId: zId.optional(),
    versionId: zId.optional(),
    party: z.record(z.string().min(1), z.number().int().positive()).optional(),
    from: zEpochMs.optional(),
    to: zEpochMs.optional(),
  },
  response: z
    .object({
      scheduleId: zId,
      slots: z.array(
        z
          .object({
            slotId: zId,
            start: zEpochMs,
            end: zEpochMs,
            status: z.enum(["DRAFT", "ON_SALE", "CLOSED", "COMPLETED", "CANCELLED"]),
            /** Present iff the request carried a party — the §2.4 verdict. */
            fits: z.boolean().optional(),
            pools: z.array(z.object({ poolId: zId, available: z.number().int().nonnegative() }).strict()),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: { scheduleId: "sch-1", productId: "sail", channelId: "web", party: { adult: 2, child: 2, bike: 2 } },
  exampleResponse: {
    scheduleId: "sch-1",
    slots: [
      { slotId: "s-1", start: 1_780_000_000_000, end: 1_780_003_600_000, status: "ON_SALE", fits: true, pools: [{ poolId: "s-1#seat", available: 42 }] },
    ],
  },
});

/**
 * B74 R1 (docs/OPERATIONS.md §7; §6, §5.4) — the operations range read:
 * per-slot per-pool occupancy across schedules. THE one place raw
 * sold/held cross the wire, and only to MANAGER/ADMIN — every buyer
 * audience keeps the availability-only wall (B20b). Counts are the
 * capacity store's Inv 2 truth at read time. Cursor pages walk the
 * (start, slotId) order; `productId` narrows to slots whose pools the
 * product's variants consume (>0 units).
 */
export const slotOccupancy = readOp({
  name: "slot.occupancy",
  permission: "supply:read",
  principals: ["STAFF:MANAGER", "STAFF:ADMIN"],
  fields: {
    serviceId: zId.optional(),
    scheduleId: zId.optional(),
    productId: zId.optional(),
    from: zEpochMs,
    to: zEpochMs,
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z
    .object({
      slots: z.array(
        z
          .object({
            slotId: zId,
            scheduleId: zId,
            serviceId: zId,
            /** B179 (B124(b) precedent): the row names its service — display name, never an id. */
            serviceName: z.string().min(1).optional(),
            start: zEpochMs,
            end: zEpochMs,
            status: z.enum(["DRAFT", "ON_SALE", "CLOSED", "COMPLETED", "CANCELLED"]),
            localDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
            pools: z.array(
              z
                .object({
                  poolId: zId,
                  /** B179: the pool's display name — two bars on one row are meaningless without it. */
                  name: z.string().min(1).optional(),
                  capacity: z.number().int().nonnegative(),
                  sold: z.number().int().nonnegative(),
                  held: z.number().int().nonnegative(),
                  available: z.number().int().nonnegative(),
                })
                .strict(),
            ),
          })
          .strict(),
      ),
      cursor: z.string().min(1).optional(),
    })
    .strict(),
  exampleRequest: { from: 1_780_000_000_000, to: 1_780_086_400_000 },
  exampleResponse: {
    slots: [
      {
        slotId: "s-1",
        scheduleId: "sch-1",
        serviceId: "svc-1",
        serviceName: "Harbor Ferry",
        start: 1_780_000_000_000,
        end: 1_780_003_600_000,
        status: "ON_SALE",
        localDate: "2026-05-29",
        pools: [{ poolId: "s-1#seat", name: "Seats", capacity: 50, sold: 42, held: 3, available: 5 }],
      },
    ],
  },
});

export const slotClose = writeOp({
  name: "slot.close",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: { slotId: zId },
  response: z.object({ slotId: zId, status: z.literal("CLOSED") }).strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: { slotId: "s-1", status: "CLOSED" },
});

export const slotReopen = writeOp({
  name: "slot.reopen",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: { slotId: zId },
  response: z.object({ slotId: zId, status: z.literal("ON_SALE") }).strict(),
  exampleRequest: { slotId: "s-1" },
  exampleResponse: { slotId: "s-1", status: "ON_SALE" },
});

/** Human-gated (§11): triggers bulk refund/rebook across confirmed orders. */
export const slotCancel = writeOp({
  name: "slot.cancel",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  gate: "human",
  fields: { slotId: zId, reason: z.string().min(1) },
  response: z.object({ slotId: zId, status: z.literal("CANCELLED"), affectedOrders: z.number().int().nonnegative() }).strict(),
  exampleRequest: { slotId: "s-1", reason: "vessel maintenance" },
  exampleResponse: { slotId: "s-1", status: "CANCELLED", affectedOrders: 17 },
});

export const poolAdjust = writeOp({
  name: "pool.adjust",
  permission: "supply:write",
  principals: ["STAFF:ADMIN"],
  fields: { poolId: zId, newCapacity: z.number().int().nonnegative() },
  response: z.object({ poolId: zId, capacity: z.number().int().nonnegative() }).strict(),
  exampleRequest: { poolId: "pool-1", newCapacity: 120 },
  exampleResponse: { poolId: "pool-1", capacity: 120 },
});

export const allocationCreate = writeOp({
  name: "allocation.create",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { allocationId: zId, poolId: zId, channelId: zId, reserved: z.number().int().positive(), releaseBackAt: zEpochMs },
  response: z.object({ allocationId: zId, status: z.literal("ACTIVE") }).strict(),
  exampleRequest: { allocationId: "alloc-1", poolId: "pool-1", channelId: "hotel-desk", reserved: 20, releaseBackAt: 1_780_100_000_000 },
  exampleResponse: { allocationId: "alloc-1", status: "ACTIVE" },
});

export const allocationAdjust = writeOp({
  name: "allocation.adjust",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  // B25 decision (2026-07-11): strictly positive — shrink-to-zero is allocation.release's job.
  fields: { allocationId: zId, newReserved: z.number().int().positive() },
  response: z.object({ allocationId: zId, reserved: z.number().int().nonnegative() }).strict(),
  exampleRequest: { allocationId: "alloc-1", newReserved: 10 },
  exampleResponse: { allocationId: "alloc-1", reserved: 10 },
});

/**
 * B164 — the pickable carve-outs behind the admin's allocation rework:
 * every allocation for the tenant (RELEASED history included), the same
 * seat and scope as the allocation writes (the B80/B92(3) precedent — no
 * authorizer amendment), sorted by allocationId. Zero carve-outs is an
 * honest empty list, never an error. Tenant-fenced by construction: the
 * store is keyed by tenantId.
 */
export const allocationList = readOp({
  name: "allocation.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      allocations: z.array(
        z
          .object({
            allocationId: zId,
            channelId: zId,
            poolId: zId,
            reserved: z.number().int().positive(),
            consumed: z.number().int().nonnegative(),
            releaseBackAt: zEpochMs,
            status: z.enum(["ACTIVE", "RELEASED"]),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    allocations: [
      { allocationId: "alloc-1", channelId: "hotel-desk", poolId: "pool-1", reserved: 20, consumed: 5, releaseBackAt: 1_780_100_000_000, status: "ACTIVE" },
    ],
  },
});

export const allocationRelease = writeOp({
  name: "allocation.release",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: { allocationId: zId },
  response: z.object({ allocationId: zId, returnedUnits: z.number().int().nonnegative() }).strict(),
  exampleRequest: { allocationId: "alloc-1" },
  exampleResponse: { allocationId: "alloc-1", returnedUnits: 10 },
});
